import { useState } from "react";
import { GiPathDistance } from "react-icons/gi";
import { MdOutlinePolyline } from "react-icons/md";
import { PiPolygonLight } from "react-icons/pi";
import { RadioGroup, RadioGroupOption } from "../base/radioGroup/radioGroup";
import { CollapsiblePanel } from "./collapsiblePanel";
import { ObjectForm } from "./objectForm";

interface IMeasurePanelProps {
  areaMeasure: (measurementUnit: string) => void;
  nonGroundDistanceMeasure: (measurementUnit: string) => void;
  groundDistanceMeasure: (measurementUnit: string) => void;
  measurementEnded: boolean;
  isMeasuring: boolean;
  onSaveMeasurement: (name: string, measureType: MeasureType) => void;
  onCancelMeasurement: () => void;
  isSaving?: boolean;
  distance: number;
}

export enum MeasureType {
  Area = "Area",
  NonGroundDistance = "NonGroundDistance",
  GroundDistance = "GroundDistance",
}

export const MeasurePanel = (props: IMeasurePanelProps) => {
  const [measureType, setMeasureType] = useState<MeasureType | null>(null);
  const [showMeasurementSavingForm, setShowMeasurementSavingForm] =
    useState(false);

  const distanceOptions: RadioGroupOption[] = [
    { label: "m", key: "meters" },
    { label: "ft", key: "feet" },
    { label: "miles", key: "miles" },
    { label: "km", key: "kilometers" },
  ];

  const groundAreaOptions: RadioGroupOption[] = [
    { label: "m&sup2;", key: "meters" },
    { label: "ft&sup2;", key: "feet" },
    { label: "yd&sup2;", key: "yards" },
    { label: "km&sup2;", key: "kilometers" },
    { label: "in&sup2;", key: "inches" },
    { label: "mi&sup2;", key: "miles" },
    { label: "ac", key: "acres" },
    { label: "ha", key: "hectares" },
  ];

  const handleChange = (value: string) => {
    switch (measureType) {
      case MeasureType.Area:
        props.areaMeasure(value);
        break;
      case MeasureType.NonGroundDistance:
        props.nonGroundDistanceMeasure(value);
        break;
      case MeasureType.GroundDistance:
        props.groundDistanceMeasure(value);
        break;
      default:
        break;
    }
  };

  const handleCancel = () => {
    setShowMeasurementSavingForm(false);
    props.onCancelMeasurement();
  };

  const handleSave = (name: string) => {
    props.onSaveMeasurement(name, measureType as MeasureType);
    setShowMeasurementSavingForm(false);
  };

  return (
    <>
      <CollapsiblePanel header="Measurements">
        <div className="mt-4 flex justify-center gap-4">
          <button
            disabled={
              measureType != null &&
              measureType != MeasureType.Area &&
              props.isMeasuring
            }
            className={`p-2 rounded-lg hover:bg-gray-200 ${
              measureType == MeasureType.Area ? "bg-gray-200" : ""
            }`}
          >
            <PiPolygonLight
              size={30}
              color="#21b9cc"
              title="Area"
              onClick={() => {
                setMeasureType(MeasureType.Area);
              }}
            />
          </button>
          <button
            disabled={
              measureType != null &&
              measureType != MeasureType.NonGroundDistance &&
              props.isMeasuring
            }
            className={`p-2 rounded-lg hover:bg-gray-200 ${
              measureType == MeasureType.NonGroundDistance ? "bg-gray-200" : ""
            }`}
          >
            <MdOutlinePolyline
              size={30}
              color="#21b9cc"
              title="Non-Ground Distance"
              onClick={() => {
                setMeasureType(MeasureType.NonGroundDistance);
              }}
            />
          </button>
          <button
            disabled={
              measureType != null &&
              measureType != MeasureType.GroundDistance &&
              props.isMeasuring
            }
            className={`p-2 rounded-lg  
            ${measureType == MeasureType.GroundDistance ? "bg-gray-200" : ""}
            `}
          >
            <GiPathDistance
              size={30}
              color="#21b9cc"
              title="Ground Distance"
              onClick={() => {
                setMeasureType(MeasureType.GroundDistance);
              }}
            />
          </button>
        </div>
        <div className="text-center text-[#b0e0e6] mt-4">
          {measureType == MeasureType.Area && (
            <div>
              <div className="pb-2">Ground Area</div>
              <div>
                <div>
                  <RadioGroup
                    options={groundAreaOptions}
                    onSelect={handleChange}
                    disabled={props.isMeasuring}
                    isMeasuring={props.isMeasuring}
                  />
                </div>
              </div>
            </div>
          )}
          {measureType == MeasureType.NonGroundDistance && (
            <div>
              <div className="pb-2">Non-Ground Distance</div>
              <div>
                <div>
                  <RadioGroup
                    options={distanceOptions}
                    onSelect={handleChange}
                    disabled={props.isMeasuring}
                    isMeasuring={props.isMeasuring}
                  />
                </div>
              </div>
            </div>
          )}
          {measureType == MeasureType.GroundDistance && (
            <div>
              <div className="pb-2">Ground Distance</div>
              <div>
                <div>
                  <RadioGroup
                    options={distanceOptions}
                    onSelect={handleChange}
                    disabled={props.isMeasuring}
                    isMeasuring={props.isMeasuring}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="text-center text-[#b0e0e6] mt-4">
          {props.measurementEnded && (
            <div className="text-[#21b9cc] text-lg font-semibold">
              {props.distance.toFixed(2)}
            </div>
          )}
        </div>
        <div className="text-center text-[#b0e0e6] mt-4">
          {props.measurementEnded && (
            <div className="flex justify-center gap-4">
              <button
                onClick={handleCancel}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowMeasurementSavingForm(true);
                }}
                className="bg-[#2fe9ff] text-white px-4 py-2 rounded-lg hover:bg-[#2fe9ff]-600 flex items-center justify-center"
              >
                Save
              </button>
            </div>
          )}
        </div>
      </CollapsiblePanel>
      <ObjectForm
        onSave={handleSave}
        onCancel={handleCancel}
        show={showMeasurementSavingForm}
        isSaving={props.isSaving}
      />
    </>
  );
};
